import { useState, type ReactNode } from "react";
import { Icon } from "../icons/Icon";

/** Rules or options in a description: one short item per line (the founder's rule: several options are a list, never a sentence). */
export function HelpList({ items }: { items: ReactNode[] }) {
  return <ul className="ob-help-list">{items.map((item, i) => <li key={i} className="ob-help-list__item">{item}</li>)}</ul>;
}

/** A short checklist of what something may and may not do (the knowledge sources round, 2026-10-22: the connector's consent screen, the key
 *  dialog's note and the MCP tab's rules): one line each, one icon a line, the kit's check for what it may do and (the sidebar and consent
 *  round, 2026-10-24) its blocked icon for what it may not; a line's quiet addition (`note`, "200 a day") follows after a middle dot. */
export type CheckItem = { text: ReactNode; ok: boolean; note?: ReactNode };
export function CheckList({ label, items }: { label: string; items: CheckItem[] }) {
  return (
    <ul className="ob-checklist" aria-label={label}>
      {items.map((x, i) => <li key={i} className={x.ok ? "ob-checklist__item" : "ob-checklist__item ob-checklist__item--no"}><Icon name={x.ok ? "check" : "blocked"} size="sm" /><span>{x.text}{x.note != null && <span className="ob-checklist__extra"> · {x.note}</span>}</span></li>)}
    </ul>
  );
}

/** Examples and the longer explanation, collapsed until asked for. A native disclosure: keyboard and screen readers work as is. */
export function More({ label = "More", children }: { label?: string; children: ReactNode }) {
  return (
    <details className="ob-more">
      <summary className="ob-more__toggle"><span className="ob-more__chevron" aria-hidden="true"><Icon name="chevronRight" size="xs" /></span>{label}</summary>
      <div className="ob-more__body">{children}</div>
    </details>
  );
}

/** A small help icon beside a label: a button named "More about <label>". A text shows in the kit's tooltip (`data-tip`) while the button is
 *  hovered or focused (the founder, 2026-10-16), and a click pins it open below the button until the next click; a line break in the text
 *  starts a new line. Rich content (not a string) shows on the click only. */
export function HelpIcon({ label, text }: { label: string; text: ReactNode }) {
  const [open, setOpen] = useState(false);
  const tip = typeof text === "string" && !open ? text : undefined; // pinned open: the note below says it, no bubble on top of it
  return (
    <span className="ob-help-icon">
      <button type="button" className="ob-help-icon__btn" aria-label={`More about ${label}`} aria-expanded={open} data-tip={tip} onClick={() => setOpen((o) => !o)}><Icon name="help" size="sm" /></button>
      {open && <span className="ob-help-icon__text" role="note">{text}</span>}
    </span>
  );
}
